<script setup lang="ts">
import type { GameShipRatingEntry } from "~~/types/gamedata";

const props = defineProps({
  shipId: {
    type: String,
    required: true,
  },
});

const { data: rating, pending } = useShipRating(() => props.shipId);

const CATEGORY_LABELS: Record<GameShipRatingEntry["category"], string> = {
  combat_potential: "Kampfpotential",
  economic_potential: "Wirtschaftspotential",
  usage_potential: "Nutzungspotential",
  "p-p_ratio": "Preis-Leistungs-Verhältnis",
  conclusion: "Schlussfolgerung",
};

const GRADE_LABELS: Record<GameShipRatingEntry["grade"], string> = {
  bad: "Schlecht",
  medium: "Mittel",
  good: "Gut",
  very_good: "Sehr gut",
};

// Nuxt-UI-Semantikfarben statt Hex — folgt automatisch dem App-Theme.
const GRADE_COLORS: Record<GameShipRatingEntry["grade"], "error" | "warning" | "success" | "primary"> = {
  bad: "error",
  medium: "warning",
  good: "success",
  very_good: "primary",
};

const GRADE_SCORES: Record<GameShipRatingEntry["grade"], number> = {
  bad: 25,
  medium: 50,
  good: 75,
  very_good: 100,
};

// "Schlussfolgerung" ist eine textliche Zusammenfassung, kein eigenständig
// bewertetes Kriterium wie die anderen vier — fliesst deshalb nicht in den
// Gesamtwert ein und wird separat/hervorgehoben dargestellt.
const scoredRatings = computed(() => (rating.value?.ratings ?? []).filter((entry) => entry.category !== "conclusion"));
const conclusion = computed(() => rating.value?.ratings.find((entry) => entry.category === "conclusion") ?? null);

const overallScore = computed(() => {
  const entries = scoredRatings.value;
  if (!entries.length) return null;
  const total = entries.reduce((sum, entry) => sum + GRADE_SCORES[entry.grade], 0);
  return Math.round(total / entries.length);
});

const overallGradeColor = computed(() => {
  const score = overallScore.value;
  if (score == null) return "primary" as const;
  if (score < 37.5) return "error" as const;
  if (score < 62.5) return "warning" as const;
  if (score < 87.5) return "success" as const;
  return "primary" as const;
});

const strengths = computed(() => (rating.value?.strengthsAndWeaknesses ?? []).filter((entry) => entry.category === "strength"));
const weaknesses = computed(() => (rating.value?.strengthsAndWeaknesses ?? []).filter((entry) => entry.category === "weakness"));

const CIRCLE_RADIUS = 15.5;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

const scoreDashoffset = computed(() => {
  const score = overallScore.value ?? 0;
  return CIRCLE_CIRCUMFERENCE * (1 - score / 100);
});
</script>

<template>
  <div class="px-2 py-4">
    <div v-if="pending" class="space-y-4">
      <USkeleton class="h-16 w-full" />
      <USkeleton class="h-40 w-full" />
    </div>

    <p v-else-if="!rating" class="px-2 py-6 text-sm text-neutral-500">
      Für dieses Schiff liegt noch keine ArisCorp-Bewertung vor.
    </p>

    <div v-else class="space-y-4">
      <!-- Autor -->
      <div class="flex items-center gap-3 rounded-lg bg-bunker-950 px-4 py-3 ring-1 ring-white/10">
        <UAvatar :src="getAssetId(rating.author?.avatar)" :alt="rating.author?.name" size="md" />
        <div class="min-w-0">
          <p class="!my-0 truncate text-sm font-semibold text-white">{{ rating.author?.name ?? "ArisCorp" }}</p>
        </div>
        <NuxtImg
          v-if="rating.author?.department?.logo"
          :src="getAssetId(rating.author.department.logo)"
          :alt="rating.author.department.name"
          width="32"
          height="32"
          class="ml-auto size-8 shrink-0 rounded object-contain opacity-80"
        />
      </div>

      <!-- Einleitung -->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div
        v-if="rating.introduction"
        class="border-l-2 border-aris-500/50 pl-4 text-sm text-neutral-300 [&_p]:my-0"
        v-html="rating.introduction"
      />

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <!-- Gesamtwertung + Stärken/Schwächen -->
        <div class="space-y-4 lg:col-span-1">
          <ShipexkursStatCard v-if="overallScore != null" title="Gesamtwertung">
            <div class="flex items-center justify-center">
              <div class="relative flex size-28 items-center justify-center">
                <svg viewBox="0 0 36 36" class="size-28 -rotate-90">
                  <circle cx="18" cy="18" :r="CIRCLE_RADIUS" fill="none" stroke="currentColor" stroke-width="3" class="text-white/10" />
                  <circle
                    cx="18"
                    cy="18"
                    :r="CIRCLE_RADIUS"
                    fill="none"
                    :stroke="`var(--ui-${overallGradeColor})`"
                    stroke-width="3"
                    stroke-linecap="round"
                    :stroke-dasharray="CIRCLE_CIRCUMFERENCE"
                    :stroke-dashoffset="scoreDashoffset"
                  />
                </svg>
                <span class="absolute text-2xl font-bold text-white">{{ overallScore }}%</span>
              </div>
            </div>
          </ShipexkursStatCard>

          <ShipexkursStatCard v-if="strengths.length || weaknesses.length" title="Stärken & Schwächen">
            <ul class="!my-0 space-y-1.5 !pl-0">
              <li v-for="(entry, index) in strengths" :key="`strength-${index}`" class="flex items-start gap-2 text-sm text-neutral-200">
                <UIcon name="i-lucide-check" class="mt-0.5 size-4 shrink-0 text-success" />
                {{ entry.name }}
              </li>
              <li v-for="(entry, index) in weaknesses" :key="`weakness-${index}`" class="flex items-start gap-2 text-sm text-neutral-200">
                <UIcon name="i-lucide-x" class="mt-0.5 size-4 shrink-0 text-error" />
                {{ entry.name }}
              </li>
            </ul>
          </ShipexkursStatCard>
        </div>

        <!-- Bewertungskategorien -->
        <div class="space-y-3 lg:col-span-2">
          <div v-for="entry in scoredRatings" :key="entry.category" class="rounded-lg bg-bunker-950 p-4 ring-1 ring-white/10">
            <div class="mb-1.5 flex items-center justify-between gap-2">
              <h4 class="!my-0 text-xs font-semibold tracking-wide text-white uppercase">{{ CATEGORY_LABELS[entry.category] }}</h4>
              <UBadge :color="GRADE_COLORS[entry.grade]" variant="subtle" size="sm">{{ GRADE_LABELS[entry.grade] }}</UBadge>
            </div>
            <p class="!my-0 text-sm text-neutral-300">{{ entry.reason }}</p>
          </div>

          <div v-if="conclusion" class="rounded-lg border border-aris-500/30 bg-aris-500/5 p-4">
            <div class="mb-1.5 flex items-center justify-between gap-2">
              <h4 class="!my-0 text-xs font-semibold tracking-wide text-aris-400 uppercase">{{ CATEGORY_LABELS.conclusion }}</h4>
              <UBadge :color="GRADE_COLORS[conclusion.grade]" variant="subtle" size="sm">{{ GRADE_LABELS[conclusion.grade] }}</UBadge>
            </div>
            <p class="!my-0 text-sm text-neutral-300">{{ conclusion.reason }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
